<template>
  <div>
    <finance-weeks-filters @apply="onApplyFilters" />
    <b-overlay :show="isLoading" rounded="sm">
      <finance-weeks-summary :summary="summary" />
      <b-card no-body>
        <b-card-body>
          <vue-good-table
            :rows="flatRows"
            :columns="columns"
            :sort-options="{ enabled: false }"
            :pagination-options="{ enabled: false }"
            :row-style-class="rowClass"
            @on-row-click="onRowClick"
          >
            <template slot="emptystate">
              <div class="text-center text-muted py-2">Нет недель за выбранный период</div>
            </template>
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'name'">
                <template v-if="props.row.kind === 'pack'">
                  <span class="font-weight-bold">{{ props.row.name }}</span>
                  <small class="text-muted d-block">{{ formatPeriod(props.row.period) }}</small>
                </template>
                <template v-else>
                  <b-link :to="weekDetailRoute(props.row.id)" class="font-weight-bold">
                    {{ props.row.name }}
                  </b-link>
                  <small class="text-muted d-block">{{ formatPeriod(props.row.period) }}</small>
                </template>
              </span>
              <span v-else-if="props.column.field === 'topics'">
                <template v-if="props.row.kind === 'pack'">
                  <div class="font-weight-bold">{{ props.row.pack_name || '—' }}</div>
                  <small>
                    <b-link
                      v-if="props.row.pack_id"
                      :href="planfixPackUrl(props.row.pack_id)"
                      target="_blank"
                      rel="noopener"
                      @click.stop
                    >
                      #{{ props.row.pack_id }}
                    </b-link>
                    <span v-else class="text-muted">—</span>
                  </small>
                </template>
                <template v-else>
                  <b-badge
                    v-for="topic in props.row.topics"
                    :key="topic.id"
                    variant="light-primary"
                    class="mr-50"
                  >
                    {{ topic.name }}
                  </b-badge>
                </template>
              </span>
              <span v-else-if="props.column.field === 'gamesPlayed'">
                {{ props.row.games_count }}
              </span>
              <span v-else-if="props.column.field === 'private'">
                <template v-if="props.row.private && props.row.private.games_count">
                  <div>{{ props.row.private.games_count }}/{{ props.row.games_count }}</div>
                  <small class="text-muted">{{ formatRub(props.row.private.revenue) }}</small>
                </template>
                <span v-else class="text-muted">—</span>
              </span>
              <span v-else-if="props.column.field === 'revenue'">{{ formatRub(props.row.revenue) }}</span>
              <span v-else-if="props.column.field === 'expenses'">
                <div><small>Х: {{ formatRub(props.row.expenses && props.row.expenses.hosts) }}</small></div>
                <div><small>А: {{ formatRub(props.row.expenses && props.row.expenses.authors) }}</small></div>
                <div><strong>≡ {{ formatRub(props.row.expenses && props.row.expenses.total) }}</strong></div>
              </span>
              <span v-else-if="props.column.field === 'profit'" :class="`text-${profitVariant(props.row.profit)}`">
                {{ formatRub(props.row.profit) }}
              </span>
              <span v-else-if="props.column.field === 'margin'">{{ formatPercent(props.row.margin) }}</span>
              <span v-else>{{ props.formattedRow[props.column.field] }}</span>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BOverlay, BBadge, BLink,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FinanceWeeksFilters from '@/views/finance/components/FinanceWeeksFilters.vue'
import FinanceWeeksSummary from '@/views/finance/components/FinanceWeeksSummary.vue'
import {
  formatRub, formatPercent, formatPeriod, profitVariant, planfixPackUrl, flattenWeeks,
} from '@/views/finance/utils'

export default {
  components: {
    BCard, BCardBody, BOverlay, BBadge, BLink,
    VueGoodTable,
    FinanceWeeksFilters, FinanceWeeksSummary,
  },
  setup(_, { root }) {
    const toast = useToast()
    const isLoading = ref(false)
    const weeks = ref([])
    const summary = ref(null)

    const flatRows = computed(() => flattenWeeks(weeks.value))

    const rowClass = row => (row.kind === 'pack' ? 'finance-row-pack' : 'finance-row-week cursor-pointer')
    const filters = ref({ from: '', to: '', onlyActive: false })

    const columns = [
      { label: 'Неделя', field: 'name' },
      { label: 'Темы', field: 'topics' },
      { label: 'Игр', field: 'gamesPlayed' },
      { label: 'Частные', field: 'private' },
      { label: 'Игроков', field: 'players_count', type: 'number' },
      { label: 'Выручка', field: 'revenue', type: 'number' },
      { label: 'Расходы', field: 'expenses' },
      { label: 'Прибыль', field: 'profit', type: 'number' },
      { label: 'Маржа', field: 'margin', type: 'number' },
    ]

    const fetchWeeks = () => {
      isLoading.value = true
      const params = {}
      if (filters.value.from) params.from = filters.value.from
      if (filters.value.to) params.to = filters.value.to
      if (filters.value.onlyActive) params.include_finished = false
      store.dispatch('app-finance/fetchWeeks', params)
        .then(response => {
          weeks.value = response.data.data || []
          summary.value = response.data.summary || null
        })
        .catch(err => {
          toast({
            component: ToastificationContent,
            props: {
              title: err.response?.data?.message || 'Ошибка загрузки финансового отчёта',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const onApplyFilters = payload => {
      filters.value = payload
      fetchWeeks()
    }

    const weekDetailRoute = id => ({ name: 'finance-week-detail', params: { id } })

    const onRowClick = ({ row }) => {
      if (row.kind === 'pack') return
      root.$router.push(weekDetailRoute(row.id))
    }

    fetchWeeks()

    return {
      isLoading,
      weeks,
      summary,
      columns,
      flatRows,
      rowClass,
      onApplyFilters,
      onRowClick,
      weekDetailRoute,
      formatRub,
      formatPercent,
      formatPeriod,
      profitVariant,
      planfixPackUrl,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.finance-row-pack > td {
  background-color: rgba(0, 0, 0, 0.025);
}

.finance-row-pack > td:first-child {
  border-top: 0;
}

.finance-row-pack > td:first-child > * {
  visibility: hidden;
}

.finance-row-pack > td:nth-child(2) {
  padding-left: 2.25rem;
}
</style>
